import React from "react";

import { useLocalization } from "@fluent/react";
import { Button, Table } from "@narmi/design_system";

import { formatReceiptAmount, formatReceiptDate } from "../format";

import styles from "./MatchedTable.module.scss";

import type { ReceiptListRow, SuggestedMatch } from "../hooks/useReceiptsList";

interface MatchedTableProps {
  rows: ReceiptListRow[];
  onRowClick?: (uuid: string) => void;
  onUnlink?: (params: { suggestionId: number; receiptId: string }) => void;
}

const HEADER_LABEL_CLASS =
  "nds-typescale--upper fontColor--primary fontWeight--bold fontSize--s";

const stopPropagation = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

interface StackedCellProps {
  primary: string;
  secondary: string;
}

const StackedCell = ({ primary, secondary }: StackedCellProps) => (
  <div className={styles["stacked-cell"]}>
    <span className="fontColor--primary fontWeight--semibold fontSize--s">
      {primary}
    </span>
    {secondary && (
      <span className="fontColor--secondary fontSize--s">{secondary}</span>
    )}
  </div>
);

interface MatchedTransactionCellProps {
  match: SuggestedMatch | null;
  emptyTestId: string;
  emptyLabel: string;
}

const MatchedTransactionCell = ({
  match,
  emptyTestId,
  emptyLabel,
}: MatchedTransactionCellProps) => {
  if (!match || !match.description) {
    return (
      <span
        className="fontColor--secondary fontSize--s"
        data-testid={emptyTestId}
      >
        {emptyLabel}
      </span>
    );
  }
  const secondary = [
    formatReceiptDate(match.date),
    formatReceiptAmount(match.amount),
  ]
    .filter(Boolean)
    .join(" · ");
  return <StackedCell primary={match.description} secondary={secondary} />;
};

const MatchedTable = ({ rows, onRowClick, onUnlink }: MatchedTableProps) => {
  const { l10n } = useLocalization();
  const showActions = Boolean(onUnlink);
  const colLayout = showActions ? "1fr 1fr auto" : "1fr 1fr";

  return (
    <div
      className={styles["matched-table-wrapper"]}
      data-testid="matched-table"
    >
      <Table
        colVisibility={showActions ? ["*", "*", "*"] : ["*", "*"]}
        colLayout={{
          s: colLayout,
          m: colLayout,
          l: colLayout,
        }}
        rowDensity="compact"
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-matched-table-receipt")}
              </span>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <span className={HEADER_LABEL_CLASS}>
                {l10n.getString("receipts-matched-table-transaction")}
              </span>
            </Table.HeaderCell>
            {showActions && (
              <Table.HeaderCell textAlign="end">
                <span className={HEADER_LABEL_CLASS}>
                  {l10n.getString("receipts-matched-table-action")}
                </span>
              </Table.HeaderCell>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row) => {
            const suggestionId = row.suggested_match?.id ?? null;
            const canUnlink = suggestionId !== null && Boolean(onUnlink);
            const handleUnlink =
              canUnlink && suggestionId !== null && onUnlink
                ? (event: React.MouseEvent) => {
                    event.stopPropagation();
                    onUnlink({ suggestionId, receiptId: row.uuid });
                  }
                : stopPropagation;
            const receiptSecondary = [
              formatReceiptDate(row.transaction_date),
              formatReceiptAmount(row.amount),
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <Table.Row
                key={row.uuid}
                onRowClick={onRowClick ? () => onRowClick(row.uuid) : undefined}
              >
                <Table.Cell>
                  <div data-testid={`matched-table-row-${row.uuid}`}>
                    <StackedCell
                      primary={
                        row.merchant ??
                        l10n.getString("receipts-table-unknown-merchant")
                      }
                      secondary={receiptSecondary}
                    />
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <MatchedTransactionCell
                    match={row.suggested_match}
                    emptyTestId="matched-table-transaction-empty"
                    emptyLabel={l10n.getString(
                      "receipts-matched-table-transaction-empty"
                    )}
                  />
                </Table.Cell>
                {showActions && (
                  <Table.Cell textAlign="end">
                    <div
                      className={styles["row-actions"]}
                      onClick={stopPropagation}
                      role="presentation"
                    >
                      <Button
                        kind="secondary"
                        size="xs"
                        label={l10n.getString("receipts-action-unlink")}
                        onClick={handleUnlink}
                        disabled={!canUnlink}
                        testId={`matched-table-unlink-${row.uuid}`}
                      />
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

export default MatchedTable;
